import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule
} from "vuex-module-decorators";

import store from "@/store";
import Service from "@/services/Cp";
import { CpPathway, CpPeranTtd, CpSession } from "@/types/cp";

@Module({
  dynamic: true,
  namespaced: true,
  store,
  name: "cp"
})
class CpStoreModule extends VuexModule {
  loading = false;
  current: CpSession | null = null;
  pathways: CpPathway[] = [];

  @Mutation
  SET_CURRENT(val: CpSession | null) {
    this.current = val;
  }

  @Mutation
  SET_PATHWAYS(val: CpPathway[]) {
    this.pathways = val;
  }

  @Mutation
  SET_LOADING(val: boolean) {
    this.loading = val;
  }

  @Mutation
  RESET_STORE() {
    this.current = null;
    this.loading = false;
  }

  @Action({ rawError: true })
  async loadPathways(): Promise<void> {
    if (this.pathways.length > 0) return;
    const response = await Service.pathways();
    this.SET_PATHWAYS(response?.data ?? []);
  }

  @Action({ rawError: true })
  async loadByRegister(registerId: string | number): Promise<void> {
    this.SET_LOADING(true);
    const response = await Service.getByRegister(registerId).finally(() => {
      this.SET_LOADING(false);
    });
    this.SET_CURRENT(response?.data ?? null);
  }

  @Action({ rawError: true })
  async save(
    payload: Partial<CpSession> & Record<string, any>
  ): Promise<CpSession | null> {
    this.SET_LOADING(true);
    const currentId = this.current?.id;
    const request =
      currentId != null
        ? Service.update(currentId, payload)
        : Service.create(payload);
    const response = await request.finally(() => {
      this.SET_LOADING(false);
    });
    const saved = response?.data ?? null;
    this.SET_CURRENT(saved);
    return saved;
  }

  @Action({ rawError: true })
  async reloadCurrent(): Promise<void> {
    const currentId = this.current?.id;
    if (currentId == null) return;
    const reloaded = await Service.find(currentId);
    this.SET_CURRENT(reloaded?.data ?? null);
  }

  @Action({ rawError: true })
  async updateActivity(payload: {
    activityId: number;
    status: string;
    variance_reason?: string | null;
    catatan?: string | null;
  }): Promise<void> {
    const currentId = this.current?.id;
    if (currentId == null) return;
    this.SET_LOADING(true);
    await Service.updateActivity(currentId, payload.activityId, {
      status: payload.status,
      variance_reason: payload.variance_reason ?? null,
      catatan: payload.catatan ?? null
    }).finally(() => this.SET_LOADING(false));
    await this.reloadCurrent();
  }

  @Action({ rawError: true })
  async bulkUpdateActivities(payload: {
    activityIds: number[];
    status: string;
  }): Promise<void> {
    const currentId = this.current?.id;
    if (currentId == null || payload.activityIds.length === 0) return;
    this.SET_LOADING(true);
    const response = await Service.bulkUpdateActivities(currentId, {
      activity_ids: payload.activityIds,
      status: payload.status
    }).finally(() => this.SET_LOADING(false));
    this.SET_CURRENT(response?.data ?? this.current);
  }

  @Action({ rawError: true })
  async addVariance(payload: Record<string, any>): Promise<void> {
    const currentId = this.current?.id;
    if (currentId == null) return;
    this.SET_LOADING(true);
    const response = await Service.storeVariance(currentId, payload).finally(
      () => this.SET_LOADING(false)
    );
    this.SET_CURRENT(response?.data ?? this.current);
  }

  @Action({ rawError: true })
  async removeVariance(varianceId: number): Promise<void> {
    const currentId = this.current?.id;
    if (currentId == null) return;
    this.SET_LOADING(true);
    const response = await Service.destroyVariance(
      currentId,
      varianceId
    ).finally(() => this.SET_LOADING(false));
    this.SET_CURRENT(response?.data ?? this.current);
  }

  @Action({ rawError: true })
  async removeSession(): Promise<void> {
    const currentId = this.current?.id;
    if (currentId == null) return;
    this.SET_LOADING(true);
    await Service.destroy(currentId).finally(() => this.SET_LOADING(false));
    this.SET_CURRENT(null);
  }

  @Action({ rawError: true })
  async sign(peran: CpPeranTtd): Promise<void> {
    const currentId = this.current?.id;
    if (currentId == null) return;
    this.SET_LOADING(true);
    const response = await Service.sign(currentId, peran).finally(() =>
      this.SET_LOADING(false)
    );
    this.SET_CURRENT(response?.data ?? this.current);
  }

  @Action({ rawError: true })
  reset(): void {
    this.RESET_STORE();
  }
}

export default getModule(CpStoreModule);
